/**
 * The only error information that may cross a service/UI boundary.
 *
 * Keep diagnostic errors (provider responses, stack traces, credential state,
 * and database details) in logs and persisted failure_message fields. Never
 * derive public copy from an arbitrary Error message.
 */
export const publicErrorCatalog = {
  system_error: {
    message: "Something went wrong. Please try again.",
  },
  file_processing_error: {
    message: "We couldn't process this file. Try again or choose another file.",
  },
  schema_mismatch: {
    message: "This file doesn't match the selected extraction schema.",
  },
  validation_error: {
    message: "Check the highlighted information and try again.",
  },
  unauthorized: {
    message: "Please sign in and try again.",
  },
  forbidden: {
    message: "You don't have permission to do that.",
  },
  not_found: {
    message: "The requested item could not be found.",
  },
  conflict: {
    message: "This item changed before your request could be completed. Refresh and try again.",
  },
  rate_limit_exceeded: {
    message: "Too many requests were made. Please wait a moment and try again.",
  },
  insufficient_tokens: {
    message: "Your account doesn't have enough processing credits for this request.",
  },
  storage_limit_exceeded: {
    message: "Your storage limit has been reached. Remove files or upgrade your plan to continue.",
  },
} as const;

export type PublicErrorCode = keyof typeof publicErrorCatalog;

export type PublicError = {
  code: PublicErrorCode;
  message: string;
  requestId?: string;
};

/** Maps only known, stable codes. Raw messages are deliberately ignored. */
export function normalizePublicError(
  code?: string | null,
  requestId?: string | null,
): PublicError {
  const normalizedCode =
    code && Object.prototype.hasOwnProperty.call(publicErrorCatalog, code)
      ? (code as PublicErrorCode)
      : "system_error";
  return {
    code: normalizedCode,
    message: publicErrorCatalog[normalizedCode].message,
    ...(requestId ? { requestId } : {}),
  };
}

export function publicErrorMessage(code?: string | null): string {
  return normalizePublicError(code).message;
}
